import { useEffect, useMemo } from 'react'

import { usePrevious } from '@/app/helpers'
import { SelectOption } from '@/app/types'

export type PaginatedQuery = {
  page: number
  size: number
}

export type PaginatedProps<Query extends PaginatedQuery> = {
  pageSizeValues?: SelectOption<number>[]
  query: Query
  root: string
  total: number

  getData?: (query: Query) => Promise<void>
  navigate: (query: Query) => void
  setPageSize: (pageSize: number) => void
}

export const usePaginated = <Query extends PaginatedQuery>(props: PaginatedProps<Query>) => {
  const { pageSizeValues = defaultPageSizeValues, query, root, total, getData, navigate, setPageSize } = props

  const previousQuery = usePrevious(query)

  useEffect(() => {
    // if page or size not changed, do nothing. We have to add ?. since the first query ref can be undefined
    if (query.page === previousQuery?.page && query.size === previousQuery?.size) {
      return
    }

    getData?.(query)
  }, [previousQuery, query, getData])

  return useMemo(
    () => ({
      namespace: root,
      page: query.page,
      size: query.size,
      total,
      pageSizeValues,
      onPageChange: (pageTo: number) => navigate({ ...query, page: pageTo }),
      onPageSizeChange: setPageSize,
    }),
    [pageSizeValues, query, root, total, navigate, setPageSize],
  )
}

const defaultPageSizeValues: SelectOption<number>[] = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '25',
    value: 25,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
]
